/// frontend/src/components/Dashboard/TeamManagement.jsx - COMPLETE FIXED VERSION
import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faUserPlus,
  faCheck,
  faTimes,
  faSpinner,
  faUserTie,
  faEnvelope,
  faPhone,
  faCalendarAlt,
  faCheckCircle,
  faExclamationTriangle,
  faSearch,
  faEye,
  faEyeSlash,
  faShieldAlt,
  faCrown,
  faClock,
  faEdit,
  faTrash,
  faSort,
  faSortUp,
  faSortDown,
  faGlobe,
  faAward,
  faBuilding,
  faHandshake,
  faLightbulb,
  faBriefcase,
  faCode,
  faUsersCog,
  faUserCheck,
  faUserClock,
  faUserSlash,
} from "@fortawesome/free-solid-svg-icons";
import API from "../../api";
import Loader from "../UI/Loader";
import TeamMemberForm from "./TeamMemberForm";
import { useAuth } from "../../context/AuthContext";

// ============================================
// TEAM ROLES CONFIGURATION
// ============================================
const TEAM_ROLES = {
  sale_representative: {
    label: "Sales Representative",
    color: "#3b82f6",
    icon: faHandshake,
  },
  advisor: {
    label: "Advisor",
    color: "#f59e0b",
    icon: faLightbulb,
  },
  coordinator: {
    label: "Coordinator",
    color: "#10b981",
    icon: faUsersCog,
  },
  assistant_advisor: {
    label: "Assistant Advisor",
    color: "#8b5cf6",
    icon: faUserCheck,
  },
  assistant_coordinator: {
    label: "Assistant Coordinator",
    color: "#06b6d4",
    icon: faUserClock,
  },
  manager: {
    label: "Manager",
    color: "#ef4444",
    icon: faBriefcase,
  },
  assistant_manager: {
    label: "Assistant Manager",
    color: "#ec4899",
    icon: faUserTie,
  },
  administrator: {
    label: "Administrator",
    color: "#6366f1",
    icon: faShieldAlt,
  },
  community_relation: {
    label: "Community & Farmer Relation",
    color: "#14b8a6",
    icon: faUsers,
  },
  developer: {
    label: "Developer",
    color: "#8b5cf6",
    icon: faCode,
  },
};

// ============================================
// MAIN COMPONENT
// ============================================
const TeamManagement = ({
  isAdmin = false,
  isSuperAdmin = false,
  canApprove = true,
  canReject = true,
  canDelete = false,
  canEdit = true,
}) => {
  const { user } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [pendingMembers, setPendingMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [message, setMessage] = useState({ type: "", text: "" });
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [showTeamMemberForm, setShowTeamMemberForm] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [viewMode, setViewMode] = useState("grid");
  const [sortField, setSortField] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    active: 0,
    byRole: {},
  });

  // ============================================
  // FETCH TEAM MEMBERS
  // ============================================
  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      setMessage({ type: "", text: "" });

      const [allResponse, pendingResponse, statsResponse] = await Promise.all([
        API.get("/users/admin/all", { params: { isTeamMember: true } }),
        API.get("/dashboard/team/pending"),
        API.get("/dashboard/team/stats"),
      ]);

      // Process all response
      if (allResponse.data?.success) {
        setTeamMembers(allResponse.data.users || allResponse.data.members || []);
      } else if (allResponse.data?.users) {
        setTeamMembers(allResponse.data.users);
      } else {
        setTeamMembers([]);
      }

      // Process pending response
      if (pendingResponse.data?.success) {
        setPendingMembers(pendingResponse.data.members || []);
      } else {
        setPendingMembers([]);
      }

      // Process stats response
      if (statsResponse.data?.success) {
        setStats(statsResponse.data.stats || {});
      } else {
        setStats({
          total: 0,
          pending: 0,
          approved: 0,
          active: 0,
          byRole: {},
        });
      }
    } catch (error) {
      console.error("Error fetching team members:", error);
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Failed to load team members",
      });
      setTeamMembers([]);
      setPendingMembers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  // ============================================
  // HANDLERS
  // ============================================
  const handleApprove = async (userId) => {
    if (!canApprove) {
      setMessage({ type: "error", text: "You don't have permission to approve" });
      return;
    }
    if (!window.confirm("Approve this team member?")) return;

    setActionLoading((prev) => ({ ...prev, [userId]: true }));
    try {
      const response = await API.put(`/dashboard/team/approve/${userId}`, {
        notes: "Approved via dashboard",
      });
      if (response.data.success) {
        setMessage({
          type: "success",
          text: "Team member approved successfully!",
        });
        fetchTeamMembers();
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Failed to approve",
      });
    } finally {
      setActionLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const handleReject = async (userId) => {
    if (!canReject) {
      setMessage({ type: "error", text: "You don't have permission to reject" });
      return;
    }
    const reason = prompt("Please provide a reason for rejection:");
    if (reason === null) return;

    setActionLoading((prev) => ({ ...prev, [userId]: true }));
    try {
      const response = await API.put(`/dashboard/team/reject/${userId}`, {
        reason,
      });
      if (response.data.success) {
        setMessage({ type: "success", text: "Team member rejected" });
        fetchTeamMembers();
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Failed to reject",
      });
    } finally {
      setActionLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const handleDelete = async (userId) => {
    if (!canDelete) {
      setMessage({ type: "error", text: "You don't have permission to delete" });
      return;
    }
    if (!window.confirm("Are you sure you want to delete this team member?"))
      return;

    setActionLoading((prev) => ({ ...prev, [userId]: true }));
    try {
      const response = await API.delete(`/users/admin/${userId}`);
      if (response.data.success) {
        setMessage({ type: "success", text: "Team member deleted" });
        fetchTeamMembers();
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Failed to delete",
      });
    } finally {
      setActionLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return faSort;
    return sortDirection === "asc" ? faSortUp : faSortDown;
  };

  // ============================================
  // FILTER & SORT
  // ============================================
  const getFilteredMembers = () => {
    let members = [...teamMembers];

    if (filter === "pending") {
      members = pendingMembers;
    } else if (filter === "approved") {
      members = members.filter((m) => m.isApproved === true);
    } else if (filter === "rejected") {
      members = members.filter(
        (m) => m.isApproved === false && m.rejectionReason,
      );
    } else if (filter === "active") {
      members = members.filter((m) => m.isActive === true);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      members = members.filter(
        (m) =>
          m.name?.toLowerCase().includes(term) ||
          m.email?.toLowerCase().includes(term) ||
          m.teamRole?.toLowerCase().includes(term) ||
          m.phone?.includes(term),
      );
    }

    members.sort((a, b) => {
      let aVal = a[sortField] || "";
      let bVal = b[sortField] || "";
      if (sortField === "createdAt") {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }
      if (typeof aVal === "string") {
        return sortDirection === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
    });

    return members;
  };

  const filteredMembers = getFilteredMembers();

  // ============================================
  // RENDER HELPERS
  // ============================================
  const getRoleLabel = (role) => TEAM_ROLES[role]?.label || role || "N/A";
  const getRoleColor = (role) => TEAM_ROLES[role]?.color || "#5A5A5A";
  const getRoleIcon = (role) => TEAM_ROLES[role]?.icon || faUserTie;

  const getStatusBadge = (member) => {
    if (!member.isApproved && member.rejectionReason) {
      return { label: "Rejected", color: "#dc2626", bg: "#fee2e2", icon: faUserSlash };
    }
    if (!member.isApproved) {
      return { label: "Pending", color: "#f59e0b", bg: "#fef3c7", icon: faClock };
    }
    if (member.isActive) {
      return { label: "Active", color: "#16a34a", bg: "#dcfce7", icon: faCheckCircle };
    }
    return { label: "Inactive", color: "#dc2626", bg: "#fee2e2", icon: faUserSlash };
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) return <Loader />;

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <div className="team-management">
      {/* Header */}
      <div className="team-management-header">
        <div className="header-left">
          <h2>
            <FontAwesomeIcon icon={faUsers} /> Team Management
            <span className="team-count">
              {stats.total || teamMembers.length} members
              {stats.pending > 0 && (
                <span className="pending-count">{stats.pending} pending</span>
              )}
            </span>
            <span className="global-badge">
              <FontAwesomeIcon icon={faGlobe} /> Globally Sourced Team
            </span>
          </h2>
        </div>
        {canEdit && (
          <div className="header-right">
            <button
              className="btn-primary"
              onClick={() => {
                setEditingMember(null);
                setShowTeamMemberForm(true);
              }}
            >
              <FontAwesomeIcon icon={faUserPlus} /> Add Team Member
            </button>
          </div>
        )}
      </div>

      {/* Message */}
      {message.text && (
        <div className={`alert-message ${message.type}`}>
          <FontAwesomeIcon
            icon={
              message.type === "success" ? faCheckCircle : faExclamationTriangle
            }
          />
          {message.text}
          <button
            className="close-btn"
            onClick={() => setMessage({ type: "", text: "" })}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      {/* Stats Summary */}
      <div className="team-stats">
        <div className="stat-card">
          <div className="stat-value">{stats.total || teamMembers.length}</div>
          <div className="stat-label">Total Members</div>
        </div>
        <div className="stat-card active">
          <div className="stat-value">{stats.active || 0}</div>
          <div className="stat-label">Active</div>
        </div>
        <div className="stat-card pending">
          <div className="stat-value">{stats.pending || 0}</div>
          <div className="stat-label">Pending Approval</div>
        </div>
        <div className="stat-card roles">
          <div className="stat-value">
            {Object.keys(stats.byRole || {}).length}
          </div>
          <div className="stat-label">Roles</div>
        </div>
        <div className="stat-card global">
          <div className="stat-value">
            <FontAwesomeIcon icon={faGlobe} />
          </div>
          <div className="stat-label">Globally Sourced</div>
        </div>
      </div>

      {/* Role Breakdown */}
      {Object.keys(stats.byRole || {}).length > 0 && (
        <div className="role-breakdown">
          {Object.entries(stats.byRole).map(([role, count]) => (
            <span key={role} className="role-chip">
              <span
                className="role-dot"
                style={{ background: getRoleColor(role) }}
              />
              <FontAwesomeIcon icon={getRoleIcon(role)} />
              {getRoleLabel(role)}: {count}
            </span>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="team-filters">
        <div className="filter-left">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Members</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="active">Active</option>
          </select>
          <div className="search-wrapper">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              placeholder="Search members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>
        <div className="filter-right">
          <button
            className="view-toggle"
            onClick={() => setViewMode(viewMode === "grid" ? "list" : "grid")}
          >
            <FontAwesomeIcon icon={viewMode === "grid" ? faEyeSlash : faEye} />
            {viewMode === "grid" ? "List" : "Grid"}
          </button>
          <button
            className="refresh-btn"
            onClick={fetchTeamMembers}
            title="Refresh"
          >
            <FontAwesomeIcon icon={faSpinner} spin={loading} />
          </button>
        </div>
      </div>

      {/* Team Members */}
      {filteredMembers.length === 0 ? (
        <div className="no-members">
          <FontAwesomeIcon icon={faUsers} size="3x" />
          <h3>No team members found</h3>
          <p>Add your first team member or adjust your filters.</p>
          {canEdit && (
            <button
              className="btn-primary"
              onClick={() => {
                setEditingMember(null);
                setShowTeamMemberForm(true);
              }}
            >
              <FontAwesomeIcon icon={faUserPlus} /> Add Team Member
            </button>
          )}
        </div>
      ) : (
        <div className={`members-${viewMode}`}>
          {filteredMembers.map((member) => {
            const status = getStatusBadge(member);
            const isPending = !member.isApproved && !member.rejectionReason;
            const roleColor = getRoleColor(member.teamRole);
            const roleLabel = getRoleLabel(member.teamRole);
            const roleIcon = getRoleIcon(member.teamRole);

            return (
              <div
                key={member._id}
                className={`member-card ${isPending ? "pending" : ""} ${member.isActive ? "active" : "inactive"}`}
              >
                <div className="member-avatar">
                  {member.profileImage || member.avatar ? (
                    <img
                      src={member.profileImage || member.avatar}
                      alt={member.name}
                    />
                  ) : (
                    <span>{getInitials(member.name)}</span>
                  )}
                  {isPending && (
                    <span className="pending-dot" title="Pending Approval">
                      ⏳
                    </span>
                  )}
                  {member.isApproved && member.isActive && (
                    <span className="active-dot" title="Active">
                      ✅
                    </span>
                  )}
                </div>

                <div className="member-info">
                  <div className="member-name">
                    {member.name}
                    {member.role === "super_admin" && (
                      <span className="super-admin-badge">
                        <FontAwesomeIcon icon={faCrown} /> Super Admin
                      </span>
                    )}
                    {member.role === "admin" && (
                      <span className="admin-badge">
                        <FontAwesomeIcon icon={faShieldAlt} /> Admin
                      </span>
                    )}
                    {member.isTeamMember && (
                      <span className="team-badge">
                        <FontAwesomeIcon icon={faUsers} /> Team
                      </span>
                    )}
                  </div>
                  <div className="member-email">
                    <FontAwesomeIcon icon={faEnvelope} /> {member.email}
                  </div>
                  {member.phone && (
                    <div className="member-phone">
                      <FontAwesomeIcon icon={faPhone} /> {member.phone}
                    </div>
                  )}
                  <div className="member-role" style={{ color: roleColor }}>
                    <FontAwesomeIcon icon={roleIcon} /> {roleLabel}
                  </div>
                  <div className="member-meta">
                    <span className="member-joined">
                      <FontAwesomeIcon icon={faCalendarAlt} />
                      Joined: {new Date(member.createdAt).toLocaleDateString()}
                    </span>
                    <span
                      className="member-status"
                      style={{ background: status.bg, color: status.color }}
                    >
                      <FontAwesomeIcon icon={status.icon} />
                      {status.label}
                    </span>
                  </div>
                  {isPending && member.rejectionReason && (
                    <div className="rejection-reason">
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                      Rejected: {member.rejectionReason}
                    </div>
                  )}
                  {member.isApproved && member.approvedAt && (
                    <div className="approved-info">
                      <FontAwesomeIcon icon={faCheckCircle} />
                      Approved: {new Date(member.approvedAt).toLocaleDateString()}
                    </div>
                  )}
                </div>

                <div className="member-actions">
                  {isPending ? (
                    <>
                      {canApprove && (
                        <button
                          className="btn-approve"
                          onClick={() => handleApprove(member._id)}
                          disabled={actionLoading[member._id]}
                        >
                          {actionLoading[member._id] ? (
                            <FontAwesomeIcon icon={faSpinner} spin />
                          ) : (
                            <FontAwesomeIcon icon={faCheck} />
                          )}
                          Approve
                        </button>
                      )}
                      {canReject && (
                        <button
                          className="btn-reject"
                          onClick={() => handleReject(member._id)}
                          disabled={actionLoading[member._id]}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                          Reject
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      {canEdit && (
                        <button
                          className="btn-edit"
                          onClick={() => {
                            setEditingMember(member);
                            setShowTeamMemberForm(true);
                          }}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          className="btn-delete"
                          onClick={() => handleDelete(member._id)}
                          disabled={actionLoading[member._id]}
                        >
                          {actionLoading[member._id] ? (
                            <FontAwesomeIcon icon={faSpinner} spin />
                          ) : (
                            <FontAwesomeIcon icon={faTrash} />
                          )}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Team Member Form Modal */}
      {showTeamMemberForm && (
        <TeamMemberForm
          member={editingMember}
          onClose={() => {
            setShowTeamMemberForm(false);
            setEditingMember(null);
          }}
          onSuccess={() => {
            setShowTeamMemberForm(false);
            setEditingMember(null);
            fetchTeamMembers();
          }}
        />
      )}
    </div>
  );
};

export default TeamManagement;